import type {
  CoreAdminProps,
  AuthProvider,
  DashboardComponent,
  LayoutComponent,
} from "ra-core";
import { withTaskCompletionEvents } from "../deals/taskCompletionEvents";
import {
  CustomRoutes,
  I18nContextProvider,
  localStorageStore,
  Resource,
} from "ra-core";
import { lazy, useEffect, useMemo, useState } from "react";
import { Route } from "react-router";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import { Admin } from "@/components/admin/admin";
import { ForgotPasswordPage } from "@/components/supabase/forgot-password-page";
import { SetPasswordPage } from "@/components/supabase/set-password-page";

import companies from "../companies";
import contacts from "../contacts";
import { Dashboard } from "../dashboard/Dashboard";
import { MobileDashboard } from "../dashboard/MobileDashboard";
import deals from "../deals";
import projects from "../projects";
import workItems from "../workItems";
import fieldDefs from "../fieldDefs";
import products from "../products";
import { Layout } from "../layout/Layout";
import { MobileLayout } from "../layout/MobileLayout";
import { SignupPage } from "../login/SignupPage";
import { ConfirmationRequired } from "../login/ConfirmationRequired";
import {
  getAuthProvider as defaultAuthProviderBuilder,
  getDataProvider as defaultDataProviderBuilder,
} from "../providers/supabase";
import sales from "../sales";
import { routePaths } from "./routePaths";
import { PublicQuotePage } from "../deals/PublicQuotePage";
import { usePublicQuoteToken } from "../deals/publicQuoteToken";
import {
  CONFIGURATION_STORE_KEY,
  type ConfigurationContextValue,
} from "./ConfigurationContext";
import type { CrmDataProvider } from "../providers/types";
import {
  defaultCompanyColors,
  defaultCompanyName,
  defaultCompanySectors,
  defaultCurrency,
  defaultTimeBillableByDefault,
  defaultDarkModeLogo,
  defaultDealCategories,
  defaultDealStages,
  defaultLightModeLogo,
  defaultNoteStatuses,
  defaultProjectStatuses,
  defaultTaskTypes,
  defaultTitle,
} from "./defaultConfiguration";
import { i18nProvider as defaulti18nProvider } from "../providers/commons/i18nProvider";
import { StartPage } from "../login/StartPage.tsx";
import { useIsMobile } from "@/hooks/use-mobile.ts";
import { createDesktopQueryClient, DEFAULT_STALE_TIME } from "./queryClient";

const defaultStore = localStorageStore(undefined, "CRM");

/**
 * Sider som lastes først når noen går til dem.
 *
 * Innloggingssidene over er med vilje ikke med: de må ligge i hovedbunten,
 * ellers får en utlogget bruker et ekstra rundturs-nettverkskall før hen ser
 * noe som helst. Alt her inne ligger derimot bak innlogging *og* bak et
 * menyvalg — ingen av dem er på veien til første skjermbilde.
 *
 * Suspense-grensen finnes allerede i `Layout`/`MobileLayout`, så rutene
 * trenger ingen egen.
 */
const ReportsPage = lazy(() =>
  import("../reports/ReportsPage").then((m) => ({ default: m.ReportsPage })),
);
const ProspectsPage = lazy(() =>
  import("../prospects/ProspectsPage").then((m) => ({
    default: m.ProspectsPage,
  })),
);
const WeekPlanPage = lazy(() =>
  import("../dashboard/focus/WeekPlanPage").then((m) => ({
    default: m.WeekPlanPage,
  })),
);
const AgreementsPage = lazy(() =>
  import("../agreements/AgreementsPage").then((m) => ({
    default: m.AgreementsPage,
  })),
);
const ProspectReportPage = lazy(() =>
  import("../prospects/ProspectReportPage").then((m) => ({
    default: m.ProspectReportPage,
  })),
);
const CompanyReportPage = lazy(() =>
  import("../companies/CompanyReportPage").then((m) => ({
    default: m.CompanyReportPage,
  })),
);
const ChangelogPage = lazy(() =>
  import("../misc/ChangelogPage").then((m) => ({ default: m.ChangelogPage })),
);
const TutorialPage = lazy(() =>
  import("../tutorial/TutorialPage").then((m) => ({ default: m.TutorialPage })),
);
const SettingsPage = lazy(() =>
  import("../settings/SettingsPage").then((m) => ({ default: m.SettingsPage })),
);
const SettingsPageMobile = lazy(() =>
  import("../settings/SettingsPageMobile").then((m) => ({
    default: m.SettingsPageMobile,
  })),
);
const ProfilePage = lazy(() =>
  import("../settings/ProfilePage").then((m) => ({ default: m.ProfilePage })),
);
const EmailComposePage = lazy(() =>
  import("../email/EmailComposePage").then((m) => ({
    default: m.EmailComposePage,
  })),
);
const EmailSentListPage = lazy(() =>
  import("../email/EmailSentListPage").then((m) => ({
    default: m.EmailSentListPage,
  })),
);
const EmailSentShowPage = lazy(() =>
  import("../email/EmailSentShowPage").then((m) => ({
    default: m.EmailSentShowPage,
  })),
);
const EmailGroupsListPage = lazy(() =>
  import("../email/EmailGroupsListPage").then((m) => ({
    default: m.EmailGroupsListPage,
  })),
);
const EmailGroupEditPage = lazy(() =>
  import("../email/EmailGroupEditPage").then((m) => ({
    default: m.EmailGroupEditPage,
  })),
);
const EmailTemplatesListPage = lazy(() =>
  import("../email/EmailTemplatesListPage").then((m) => ({
    default: m.EmailTemplatesListPage,
  })),
);
const EmailTemplateEditPage = lazy(() =>
  import("../email/EmailTemplateEditPage").then((m) => ({
    default: m.EmailTemplateEditPage,
  })),
);
const DialogPage = lazy(() =>
  import("../email/dialog/DialogPage").then((m) => ({
    default: m.DialogPage,
  })),
);

// Mobilvarianter av ressurssider. Desktop-varianten kommer fra
// ressursmodulene (`../contacts` osv.), som gjør det samme der.
const ContactListMobile = lazy(() =>
  import("../contacts/ContactList").then((m) => ({
    default: m.ContactListMobile,
  })),
);
const ContactShow = lazy(() =>
  import("../contacts/ContactShow").then((m) => ({ default: m.ContactShow })),
);
const CompanyShow = lazy(() =>
  import("../companies/CompanyShow").then((m) => ({ default: m.CompanyShow })),
);
const NoteShowPage = lazy(() =>
  import("../notes/NoteShowPage").then((m) => ({ default: m.NoteShowPage })),
);

export type CRMProps = {
  dataProvider?: CrmDataProvider;
  authProvider?: AuthProvider;
  i18nProvider?: CoreAdminProps["i18nProvider"];
  store?: CoreAdminProps["store"];
  dashboard?: DashboardComponent;
  layout?: LayoutComponent;
} & Partial<ConfigurationContextValue>;

/**
 * CRM Component
 *
 * This component sets up and renders the main CRM application using `ra-core`. It provides
 * default configurations and themes but allows for customization through props. The component
 * seeds the store with any custom prop values for backwards compatibility.
 *
 * @param {LabeledValue[]} companySectors - The list of company sectors used in the application.
 * @param {string} currency - The ISO 4217 currency code used to format monetary values (e.g. "USD", "EUR", "GBP").
 * @param {RaThemeOptions} darkTheme - The theme to use when the application is in dark mode.
 * @param {LabeledValue[]} dealCategories - The categories of deals used in the application.
 * @param {DealStage[]} dealStages - The stages of deals used in the application.
 * @param {RaThemeOptions} lightTheme - The theme to use when the application is in light mode.
 * @param {string} logo - The logo used in the CRM application.
 * @param {NoteStatus[]} noteStatuses - The statuses of notes used in the application.
 * @param {LabeledValue[]} taskTypes - The types of tasks used in the application.
 * @param {string} title - The title of the CRM application.
 *
 * @returns {JSX.Element} The rendered CRM application.
 *
 * @example
 * // Basic usage of the CRM component
 * import { CRM } from '@/components/mimin/root/CRM';
 *
 * const App = () => (
 *     <CRM
 *         logo="/path/to/logo.png"
 *         title="My Custom CRM"
 *         lightTheme={{
 *             ...defaultTheme,
 *             palette: {
 *                 primary: { main: '#0000ff' },
 *             },
 *         }}
 *     />
 * );
 *
 * export default App;
 */
export const CRM = ({
  companySectors = defaultCompanySectors,
  companyColors = defaultCompanyColors,
  companyName = defaultCompanyName,
  currency = defaultCurrency,
  timeBillableByDefault = defaultTimeBillableByDefault,
  dealCategories = defaultDealCategories,
  dealStages = defaultDealStages,
  darkModeLogo = defaultDarkModeLogo,
  lightModeLogo = defaultLightModeLogo,
  noteStatuses = defaultNoteStatuses,
  projectStatuses = defaultProjectStatuses,
  taskTypes = defaultTaskTypes,
  title = defaultTitle,
  dataProvider = defaultDataProviderBuilder(),
  authProvider = defaultAuthProviderBuilder(),
  i18nProvider = defaulti18nProvider,
  store = defaultStore,
  googleWorkplaceDomain = import.meta.env.VITE_GOOGLE_WORKPLACE_DOMAIN,
  disableEmailPasswordAuthentication = import.meta.env
    .VITE_DISABLE_EMAIL_PASSWORD_AUTHENTICATION === "true",
  ...rest
}: CRMProps) => {
  // Seed the store with CRM prop values if not already stored
  // (backwards compatibility for prop-based config)
  useEffect(() => {
    if (!store.getItem(CONFIGURATION_STORE_KEY)) {
      store.setItem(CONFIGURATION_STORE_KEY, {
        companySectors,
        companyColors,
        companyName,
        currency,
        timeBillableByDefault,
        dealCategories,
        dealStages,
        noteStatuses,
        projectStatuses,
        taskTypes,
        title,
        darkModeLogo,
        lightModeLogo,
        googleWorkplaceDomain,
        disableEmailPasswordAuthentication,
      } satisfies ConfigurationContextValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store]);

  const isMobile = useIsMobile();
  // Én QueryClient for hele levetiden til CRM, slik at cachen overlever
  // re-rendringer og at desktopvisningen monteres på nytt.
  const [desktopQueryClient] = useState(createDesktopQueryClient);

  // on login, pre-fetch the configuration to avoid a flickering
  // when accessing the app for the first time
  const wrappedAuthProvider = useMemo<AuthProvider>(
    () => ({
      ...authProvider,
      login: async (params: any) => {
        const result = await authProvider.login(params);
        try {
          const config = await dataProvider.getConfiguration();
          if (Object.keys(config).length > 0) {
            store.setItem(CONFIGURATION_STORE_KEY, config);
          }
        } catch {
          // Non-critical: config will load via useConfigurationLoader
        }
        return result;
      },
      handleCallback: async (params: any) => {
        if (!authProvider.handleCallback) {
          throw new Error(
            "handleCallback is not implemented in the authProvider",
          );
        }
        const result = await authProvider.handleCallback(params);
        try {
          const config = await dataProvider.getConfiguration();
          if (Object.keys(config).length > 0) {
            store.setItem(CONFIGURATION_STORE_KEY, config);
          }
        } catch {
          // Non-critical: config will load via useConfigurationLoader
        }
        return result;
      },
      logout: async (params: any) => {
        try {
          store.removeItem(CONFIGURATION_STORE_KEY);
        } catch {
          // Ignore
        }
        return authProvider.logout(params);
      },
    }),
    [authProvider, dataProvider, store],
  );

  // Fullførte oppgaver på muligheter åpner «Hva er neste steg?».
  const eventDataProvider = useMemo(
    () => withTaskCompletionEvents(dataProvider),
    [dataProvider],
  );

  const ResponsiveAdmin = isMobile ? MobileAdmin : DesktopAdmin;

  // Kundelenken til et tilbud: uten innlogging og uten resten av Mimin.
  const quoteToken = usePublicQuoteToken();
  if (quoteToken) {
    return (
      <I18nContextProvider value={i18nProvider}>
        <PublicQuotePage token={quoteToken} dataProvider={dataProvider} />
      </I18nContextProvider>
    );
  }

  return (
    <ResponsiveAdmin
      {...(isMobile ? {} : { queryClient: desktopQueryClient })}
      dataProvider={eventDataProvider}
      authProvider={wrappedAuthProvider}
      i18nProvider={i18nProvider}
      store={store}
      loginPage={StartPage}
      requireAuth
      {...rest}
    />
  );
};

const DesktopAdmin = (
  props: CoreAdminProps & {
    dashboard?: DashboardComponent;
    layout?: LayoutComponent;
  },
) => {
  return (
    <Admin
      layout={props.layout ?? Layout}
      dashboard={props.dashboard ?? Dashboard}
      {...props}
    >
      <CustomRoutes noLayout>
        <Route path={SignupPage.path} element={<SignupPage />} />
        <Route
          path={ConfirmationRequired.path}
          element={<ConfirmationRequired />}
        />
        <Route path={SetPasswordPage.path} element={<SetPasswordPage />} />
        <Route
          path={ForgotPasswordPage.path}
          element={<ForgotPasswordPage />}
        />
      </CustomRoutes>

      <CustomRoutes>
        <Route path={routePaths.profile} element={<ProfilePage />} />
        <Route path={routePaths.settings} element={<SettingsPage />} />
        <Route path={routePaths.changelog} element={<ChangelogPage />} />
        <Route path={routePaths.tutorial} element={<TutorialPage />} />
        <Route path={routePaths.reports} element={<ReportsPage />} />
        <Route path={routePaths.prospects} element={<ProspectsPage />} />
        <Route path={routePaths.agreements} element={<AgreementsPage />} />
        <Route path={routePaths.weekPlan} element={<WeekPlanPage />} />
        <Route
          path={routePaths.prospectReport}
          element={<ProspectReportPage />}
        />
        <Route
          path={routePaths.companyReport}
          element={<CompanyReportPage />}
        />
        <Route path={routePaths.emailCompose} element={<EmailComposePage />} />
        <Route
          path={routePaths.emailSentList}
          element={<EmailSentListPage />}
        />
        <Route
          path={routePaths.emailSentShow}
          element={<EmailSentShowPage />}
        />
        <Route
          path={routePaths.emailGroupsList}
          element={<EmailGroupsListPage />}
        />
        <Route
          path={routePaths.emailGroupNew}
          element={<EmailGroupEditPage />}
        />
        <Route
          path={routePaths.emailGroupEdit}
          element={<EmailGroupEditPage />}
        />
        <Route
          path={routePaths.emailTemplatesList}
          element={<EmailTemplatesListPage />}
        />
        <Route
          path={routePaths.emailTemplateNew}
          element={<EmailTemplateEditPage />}
        />
        <Route
          path={routePaths.emailTemplateEdit}
          element={<EmailTemplateEditPage />}
        />
        <Route path={routePaths.emailDialog} element={<DialogPage />} />
        <Route path={routePaths.emailDialogTab} element={<DialogPage />} />
      </CustomRoutes>
      <Resource name="deals" {...deals} />
      <Resource name="contacts" {...contacts} />
      <Resource name="companies" {...companies} />
      <Resource name="projects" {...projects} />
      <Resource name="work_items" {...workItems} />
      <Resource name="sections" recordRepresentation="name" />
      <Resource name="field_defs" {...fieldDefs} />
      <Resource name="field_values" />
      <Resource name="products" {...products} />
      <Resource name="company_products" />
      <Resource name="contact_notes" />
      <Resource name="deal_notes" />
      <Resource name="tasks" />
      <Resource name="sales" {...sales} />
      <Resource name="tags" />
      <Resource name="saved_views" />
      <Resource name="dashboard_notes" />
      <Resource name="prospect_overview" />
      <Resource name="prospect_segments" />
      <Resource name="company_last_contact" />
      <Resource name="routine_runs" />
      <Resource name="company_next_steps" />
      <Resource name="company_economy" />
      <Resource name="email_recipients" />
      <Resource name="dialog_surveys" />
      <Resource name="dialog_questions" />
      <Resource name="dialog_invitations" />
      <Resource name="dialog_responses" />
      <Resource name="dialog_responses_overview" />
    </Admin>
  );
};

const MobileAdmin = (
  props: CoreAdminProps & {
    dashboard?: DashboardComponent;
    layout?: LayoutComponent;
  },
) => {
  // Opprettes én gang per montering. Tidligere ble både klient og persister
  // laget på nytt ved hver rendring, og cachen ble kastet hver gang.
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            gcTime: 1000 * 60 * 60 * 24, // 24 hours
            staleTime: DEFAULT_STALE_TIME,
            networkMode: "offlineFirst",
          },
          mutations: {
            networkMode: "offlineFirst",
          },
        },
      }),
  );
  const [asyncStoragePersister] = useState(() =>
    createAsyncStoragePersister({
      storage: localStorage,
    }),
  );

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{ persister: asyncStoragePersister }}
    >
      <Admin
        queryClient={queryClient}
        layout={props.layout ?? MobileLayout}
        dashboard={props.dashboard ?? MobileDashboard}
        {...props}
      >
        <CustomRoutes noLayout>
          <Route path={SignupPage.path} element={<SignupPage />} />
          <Route
            path={ConfirmationRequired.path}
            element={<ConfirmationRequired />}
          />
          <Route path={SetPasswordPage.path} element={<SetPasswordPage />} />
          <Route
            path={ForgotPasswordPage.path}
            element={<ForgotPasswordPage />}
          />
        </CustomRoutes>
        <CustomRoutes>
          <Route path={routePaths.settings} element={<SettingsPageMobile />} />
          <Route path={routePaths.changelog} element={<ChangelogPage />} />
          <Route path={routePaths.tutorial} element={<TutorialPage />} />
          <Route path={routePaths.reports} element={<ReportsPage />} />
          <Route path={routePaths.prospects} element={<ProspectsPage />} />
          <Route path={routePaths.agreements} element={<AgreementsPage />} />
          <Route path={routePaths.weekPlan} element={<WeekPlanPage />} />
          <Route
            path={routePaths.emailCompose}
            element={<EmailComposePage />}
          />
          <Route
            path={routePaths.emailSentList}
            element={<EmailSentListPage />}
          />
          <Route
            path={routePaths.emailSentShow}
            element={<EmailSentShowPage />}
          />
          <Route
            path={routePaths.emailGroupsList}
            element={<EmailGroupsListPage />}
          />
          <Route
            path={routePaths.emailGroupNew}
            element={<EmailGroupEditPage />}
          />
          <Route
            path={routePaths.emailGroupEdit}
            element={<EmailGroupEditPage />}
          />
          <Route
            path={routePaths.emailTemplatesList}
            element={<EmailTemplatesListPage />}
          />
          <Route
            path={routePaths.emailTemplateNew}
            element={<EmailTemplateEditPage />}
          />
          <Route
            path={routePaths.emailTemplateEdit}
            element={<EmailTemplateEditPage />}
          />
          <Route path={routePaths.emailDialog} element={<DialogPage />} />
          <Route path={routePaths.emailDialogTab} element={<DialogPage />} />
        </CustomRoutes>
        <Resource
          name="contacts"
          list={ContactListMobile}
          show={ContactShow}
          recordRepresentation={contacts.recordRepresentation}
        >
          <Route path=":id/notes/:noteId" element={<NoteShowPage />} />
        </Resource>
        <Resource name="companies" list={companies.list} show={CompanyShow} />
        <Resource name="deals" list={deals.list} show={deals.list} />
        <Resource name="projects" {...projects} />
        <Resource name="work_items" {...workItems} />
        <Resource name="sections" recordRepresentation="name" />
        <Resource name="field_defs" {...fieldDefs} />
        <Resource name="field_values" />
        <Resource name="time_entries" />
        <Resource name="company_notes" />
        {/* Kontaktpåminnelser (tasks) er skjult i UI (PR-H2); ressursen
            beholdes for datalaget (kontaktfletting). */}
        <Resource name="tasks" />
        <Resource name="saved_views" />
        <Resource name="dashboard_notes" />
        <Resource name="prospect_overview" />
        <Resource name="prospect_segments" />
        <Resource name="company_last_contact" />
        <Resource name="routine_runs" />
        <Resource name="company_next_steps" />
        <Resource name="company_economy" />
        <Resource name="email_recipients" />
        <Resource name="dialog_surveys" />
        <Resource name="dialog_questions" />
        <Resource name="dialog_invitations" />
        <Resource name="dialog_responses" />
        <Resource name="dialog_responses_overview" />
      </Admin>
    </PersistQueryClientProvider>
  );
};
